/* global userId donorId teacherId */ // pageSpecificJsVariables
import { isLoggedIn, isLoggedInOrRemembered } from './isLoggedIn.ts';

/**
 * These functions replicates logic found in TeacherProfilePhotoURLManager.java#getTeacherProfilePhotoURL
 * Note: Max sizeInPixels should match PhotoUrlHelperService.java#MAX_WIDTH and MAX_HEIGHT (800px)
 */
export const getS3ProfilePhotoURL = function (photoPublished, photoType, userPhotoId, photoDate, photoPath, sizeInPixels, baseCacheURL, baseS3URL,
    preFastlyImage, smartCrop, xCoord, yCoord, cropWidth, cropHeight) {
    if (!photoPublished) {
        return baseCacheURL + 'images/placeholder-avatars/' + sizeInPixels + '/teacher-placeholder-' + Math.floor(((userId % 20) + 2) / 2) + '_' + sizeInPixels + '.png?auto=webp';
    }
    if (sizeInPixels > 800) {
        sizeInPixels = 800;
    }
    const photoURL = getTeacherProfilePhotoFromPhotoDetails(preFastlyImage, photoPath, photoType, userPhotoId);
    const fastlyImageUrlParams = getFastlyImageParamsFromPhotoCropDetails(sizeInPixels, sizeInPixels, smartCrop, xCoord, yCoord, cropWidth, cropHeight, photoDate);
    return baseS3URL + photoURL + fastlyImageUrlParams;
};

/**
 * This function replicates logic found in PhotoUrlHelperService.java#.getResizedTeacherPhotoURL
 */
const getTeacherProfilePhotoFromPhotoDetails = function (preFastlyImage, photoPath, photoType, userPhotoId) {
    const TEACHER_PROFILE_SRC_SIZE_POST_FASTLY = 'orig';
    const TEACHER_PROFILE_SRC_SIZE_PRE_FASTLY = '272x272';

    let teacherProfilePhotoDir;
    let teacherProfilePhotoSuffix;
    if (preFastlyImage) {
        teacherProfilePhotoDir = TEACHER_PROFILE_SRC_SIZE_PRE_FASTLY;
        teacherProfilePhotoSuffix = TEACHER_PROFILE_SRC_SIZE_PRE_FASTLY;
    } else {
        teacherProfilePhotoDir = TEACHER_PROFILE_SRC_SIZE_POST_FASTLY;
        teacherProfilePhotoSuffix = TEACHER_PROFILE_SRC_SIZE_POST_FASTLY;
    }
    return photoPath + '/' + teacherProfilePhotoDir + '/tp' + userPhotoId + '_' + teacherProfilePhotoSuffix + '.' + photoType;
};

/**
 * This function replicates logic found in PhotoUrlHelperService.java#getFastlyImageApiParamsWithoutPhotoCropDetails
 */
const getFastlyImageParamsFromPhotoCropDetails = function (targetWidth, targetHeight, smartCrop, xCoord, yCoord, cropWidth, cropHeight, photoModDate) {
    const CROP_INSTRUCTION_PARAM = 'crop';
    const SMART_CROP_PARAM = 'crop=1:1,smart';
    const CROP_X_COORD_PARAM = 'x';
    const CROP_Y_COORD_PARAM = 'y';
    const IMAGE_WIDTH_PARAM = 'width';
    const IMAGE_HEIGHT_PARAM = 'height';
    const IMAGE_FIT_WITHOUT_CROP_PARAM_AND_VALUE = 'fit=bounds';
    const AUTO_IMAGE_WEBP_PARAM_AND_VALUE = 'auto=webp';

    let params = '?';
    if (smartCrop) {
        params += SMART_CROP_PARAM;
        params += '&';
    } else if (cropWidth > 0 && cropHeight > 0) {
        params += CROP_INSTRUCTION_PARAM;
        params += '=' + cropWidth;
        params += ',' + cropHeight;
        params += ',' + CROP_X_COORD_PARAM + xCoord;
        params += ',' + CROP_Y_COORD_PARAM + yCoord;
        params += '&';
    }
    params += IMAGE_WIDTH_PARAM + '=' + targetWidth + '&';
    params += IMAGE_HEIGHT_PARAM + '=' + targetHeight + '&';
    if (!smartCrop) {
        params += IMAGE_FIT_WITHOUT_CROP_PARAM_AND_VALUE + '&';
    }
    params += AUTO_IMAGE_WEBP_PARAM_AND_VALUE + '&';
    params += 't=' + photoModDate;
    return params;
};

/**
 * @param {string} smallPath The small user photo URL that's returned by our API
 * @return {string} The path to the medium small photo URL
 */
export const convertSmallPhotoToMediumSmall = function (smallPath) {
    return smallPath && smallPath.replace(/\bsm\b/, 'medsm');
};

const getUserPhotoSizes = function () {
    const photoSizes = {};
    photoSizes.retina = { dir: 'retina', filename: '_retina' };
    photoSizes.medsmall = { dir: 'medsmall', filename: '_mdsm' };
    photoSizes.med = { dir: 'med', filename: '_md' };
    photoSizes.small = { dir: 'small', filename: '_sm' };
    photoSizes.thumbnail = { dir: 'thumbnail', filename: '_thmb' };
    return photoSizes;
};

const userPhotoSizes = getUserPhotoSizes();

export const isUserTheTeacher = function () {
    // 'userId' is defined in userInfo.js
    const userIdExists = typeof userId !== 'undefined' && userId;
    const teacherIdExists = typeof teacherId !== 'undefined' && teacherId;
    return userIdExists && teacherIdExists && teacherId === userId;
};

export const isUserThisDonor = function (donorId) {
    // 'userId' is defined in userInfo.js
    const userIdExists = typeof userId !== 'undefined' && !!userId;
    return userIdExists && donorId === userId;
};

/**
 * Checks whether we need to authenticate a user before they take an action.
 * If works for either donors or teachers, depending on whether teacherId or donorId is defined.
 */
export function requireLoginForPageUpdate() {
    if (typeof teacherId !== 'undefined') {
        return !isLoggedInOrRemembered() || userId !== teacherId;
    } else if (typeof donorId !== 'undefined') {
        // This branch calls isLoggedIn instead of isLoggedInOrRemembered because
        // archiving a giving page requires the user to be fully auth'd
        // (see givingPageOpen.js)
        return !isLoggedIn() || userId !== donorId;
    }
    return !isLoggedInOrRemembered();
}
